/**
 * @file Base layer mixin that adds common layer functionality and basic jepTools
 */

import { Type } from '@angular/core';
import {
  property,
  subclass
} from '@arcgis/core/core/accessorSupport/decorators';
import esriLayer from '@arcgis/core/layers/Layer';
import {
  AbstractType,
  JepToolsLayerInterface,
  JepToolsMixin,
  LayerJepTools
} from './jep-tools.mixin';

/**
 * Properties that can be passed to initialize a base layer
 */
export interface BaseLayerProperties {
  /** Layer display order (z-index) */
  zOrder?: number;
  /** Additional metadata key-value pairs */
  metadata?: Record<string, any>;
  /** Display label for the layer */
  label?: string;
}

/**
 * Interface describing the shape of a base layer
 */
export interface BaseLayerInterface {
  /** Layer display order (z-index) */
  zOrder: number;
  /** Additional metadata key-value pairs */
  metadata: Record<string, any>;
  /** Display label for the layer */
  label?: string;
}

/**
 * Basic jepTools interface with core layer functionality
 * @extends LayerJepTools
 */
export type BaseJepTools = LayerJepTools<
  esriLayer,
  {
    toggleVisibility: (visible?: boolean) => boolean;
  }
>;

/**
 * Mixin that adds base layer functionality to an ArcGIS layer.
 * Provides common properties like zOrder and metadata, along with basic jepTools methods.
 *
 * @template TBase - Constructor type of the base layer
 * @param Base - The base layer constructor to extend
 * @returns A new class that extends the base layer with common functionality
 *
 * @example
 * class MyLayer extends BaseLayerMixin(EsriLayer) {
 *   constructor(props: BaseLayerProperties) {
 *     super(props);
 *   }
 * }
 *
 * const layer = new MyLayer({
 *   zOrder: 1,
 *   metadata: { source: 'example' },
 *   label: 'My Layer'
 * });
 *
 * layer.jepTools.toggleVisibility();
 * console.log(layer.zOrder);
 */
export function BaseLayerMixin<TBase extends AbstractType<esriLayer>>(
  Base: TBase
): Type<
  InstanceType<TBase> &
    BaseLayerInterface &
    JepToolsLayerInterface<BaseJepTools>
> {
  const baseJepTools: BaseJepTools = {
    toggleVisibility(this: esriLayer, visible?: boolean) {
      this.visible = visible ?? !this.visible;
      return this.visible;
    }
  };

  @subclass('jmkw.layers.BaseLayer')
  class BaseLayer extends JepToolsMixin<Type<esriLayer>, BaseJepTools>(
    Base as unknown as Type<esriLayer>,
    baseJepTools
  ) {
    constructor(...args: any[]) {
      super(...args); // Spread the args
      if (args?.[0]) {
        const { zOrder, metadata, label, ...props } = args[0];
        if (zOrder !== undefined) this.zOrder = zOrder;
        if (metadata) this.metadata = metadata;
        if (label) this.label = label;
        // Pass through other properties to maintain type and other base properties
        // Apply all other properties to the instance, excluding uid and layers which are read-only
        const { uid, layers, ...assignableProps } = props;
        Object.assign(this, assignableProps);
      }
    }

    @property()
    zOrder = 0;

    @property()
    metadata: Record<string, any> = {};

    @property()
    label?: string;
  }

  return BaseLayer as unknown as Type<
    InstanceType<TBase> &
      BaseLayerInterface &
      JepToolsLayerInterface<BaseJepTools>
  >;
}

/**
 * Creates a layer class with extended jepTools functionality.
 * Combines the base layer mixin with additional jepTools methods while maintaining proper typing.
 *
 * Helper function use when creating custom layer classes with additional functionality of more complexity.
 *
 * @template TBase - Constructor type of the base layer
 * @template TJepTools - Type describing the additional jepTools methods to add
 * @template TExtension - Optional type describing additional properties added to the base layer and referenced in jepTools methods
 *
 * @param baseLayer - The base ArcGIS layer class to extend
 * @param jepTools - Object containing the additional jepTools method implementations
 * @returns A new class extending the base layer with combined jepTools functionality
 *
 * @example
 * class CustomLayer extends createLayerWithJepTools<
 *   Type<esriLayer>,
 *   {
 *     customMethod(value: string): { customProp = value; };
 *   },
 *   { customProp: string }
 * >(esriLayer, {
 *   customMethod(value: string) {
 *     console.log(this.customProp, value);
 *   }
 * }) {
 *   customProp = 'test';
 * }
 */
export function createLayerWithJepTools<
  TBase extends AbstractType<esriLayer>,
  TJepTools extends Record<string, (...args: any[]) => any>,
  TExtension = Record<string, any>
>(
  baseLayer: TBase,
  jepTools: {
    [K in keyof TJepTools]: (
      this: InstanceType<TBase> & TExtension,
      ...args: Parameters<TJepTools[K]>
    ) => ReturnType<TJepTools[K]>;
  }
) {
  // First apply base layer mixin
  const baseWithMixin = BaseLayerMixin(baseLayer);
  // Then apply jepTools with the combined type
  return JepToolsMixin<typeof baseWithMixin, TJepTools>(
    baseWithMixin,
    jepTools as TJepTools
  );
}
